import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { ThreadMessage, ThreadParams, useAuth, useThread } from '@screentone/addon-auth-wrapper';

import { AvailableFeatureFlags } from 'data/generated/graphql';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { LOCK_PAYLOAD_VERSION, PAGE_LOCK_THREAD_PREFIX } from 'utils/constants';
import { getUserId } from 'utils/user';

export type LockStateUser = {
  id: string;
  name: string;
};

export type LockState = {
  user: LockStateUser | null;
  timestamp: number;
  forced: boolean;
  actor: string;
  version: number;
};

export type FragmentsLockState = Record<string, LockState | undefined>;

export type FragmentStateData = {
  state: LockState | undefined;
  prevState: LockState | undefined;
  lockedBy: LockStateUser | null;
  isLocked: boolean;
  isLockedByMe: boolean;
  wasLocked: boolean;
  wasLockedByMe: boolean;
  isReadOnly: boolean;
  hasTakenOver: boolean;
};

export interface PageLockState {
  state: Record<string, FragmentStateData>;
  lockPageFragment: (fragmentId: string, forced?: boolean) => Promise<ThreadMessage | null>;
  releasePageFragment: (fragmentId: string, forcedBySystem?: boolean) => Promise<ThreadMessage | null>;
  loading: boolean;
  refetcher?: () => void;
}

const DEFAULT_FRAGMENT_STATE: FragmentStateData = {
  state: undefined,
  prevState: undefined,
  lockedBy: null,
  isLocked: false,
  isLockedByMe: false,
  wasLocked: false,
  wasLockedByMe: false,
  isReadOnly: false,
  hasTakenOver: false
};

const DEFAULT_PAGE_LOCK_CONTEXT: PageLockState = {
  state: { publishing: DEFAULT_FRAGMENT_STATE },
  lockPageFragment: () => Promise.resolve(null),
  releasePageFragment: () => Promise.resolve(null),
  loading: false,
  refetcher: () => {}
};

export const PageLockContext = createContext(DEFAULT_PAGE_LOCK_CONTEXT);

export function getPageLockThreadId(threadId: string): string {
  return `${PAGE_LOCK_THREAD_PREFIX}${threadId}`;
}

interface PageLockProviderProps {
  allessehId: string;
  fragments: readonly string[];
  featureFlag: AvailableFeatureFlags;
  children: React.ReactNode;
  refetcher?: () => void;
  skipInitialFetch?: boolean;
  initialMessages?: ThreadMessage[];
}

export const PageLockProvider = ({
  children,
  allessehId,
  fragments,
  featureFlag,
  refetcher,
  initialMessages,
  skipInitialFetch
}: PageLockProviderProps) => {
  const isLockingEnabled = useFeatureFlag(featureFlag, false);
  const params: ThreadParams = useMemo(
    () => ({
      threadId: `${PAGE_LOCK_THREAD_PREFIX}${allessehId}`,
      sort: 'DESC',
      limit: 1,
      initialMessages,
      skipInitialFetch
    }),
    [allessehId, skipInitialFetch, initialMessages]
  );
  const { threadMessages, sendThreadMessage, fetchLatest, loading, refetchThread } = useThread(params);

  const { user: currentUser } = useAuth();
  const lockingUser = useMemo(
    () => ({
      id: getUserId(currentUser),
      name: currentUser?.name ?? 'Unknown User'
    }),
    [currentUser]
  );

  const [lockState, setLockState] = useState<FragmentsLockState>({});
  const [prevLockState, setPrevLockState] = useState<FragmentsLockState>({});

  const fragmentState = useCallback(
    (fragmentId: string): FragmentStateData => {
      const currentLockState = lockState[fragmentId];
      const previousLockState = prevLockState[fragmentId];

      // UI state derived from thread messages
      const lockedBy = currentLockState?.user ?? null;
      const prevLockedBy = previousLockState?.user ?? null;

      const isLocked = lockedBy !== null;
      const wasLocked = prevLockedBy !== null;

      const isLockedByMe = isLocked && lockedBy.id === lockingUser.id;
      const wasLockedByMe = wasLocked && prevLockedBy.id === lockingUser.id;

      const isReadOnly = isLockingEnabled && !isLockedByMe;
      const hasTakenOver = Boolean(!isLockedByMe && currentLockState?.forced && wasLockedByMe);

      return {
        state: currentLockState,
        prevState: previousLockState,
        lockedBy,
        isLocked,
        isLockedByMe,
        wasLocked,
        wasLockedByMe,
        isReadOnly,
        hasTakenOver
      };
    },
    [lockState, lockingUser.id, prevLockState, isLockingEnabled]
  );

  const prepareLockMessage = useCallback(
    (fragmentId: string, forced: boolean = false): Record<string, LockState> => ({
      [fragmentId]: {
        user: lockingUser,
        timestamp: Date.now(),
        forced,
        actor: lockingUser.id,
        version: LOCK_PAYLOAD_VERSION
      }
    }),
    [lockingUser]
  );

  const lockPageFragment = useCallback(
    async (fragmentId: string, forced: boolean = false) => {
      // Fetch the latest state for the thread
      const [latest = null] = await fetchLatest();
      const updatedFragmentLockState = (latest ? latest.payload : {}) as FragmentsLockState;
      const currentLock = updatedFragmentLockState[fragmentId];

      // Only send a lock message if the fragment is currently unlocked, or if we're forcing the lock
      if (!currentLock?.user || forced) {
        const result = await sendThreadMessage({
          ...updatedFragmentLockState,
          ...prepareLockMessage(fragmentId, forced)
        });

        await refetchThread();

        return result;
      }

      if (currentLock.user.id !== lockingUser.id) {
        console.warn(`Fragment <${fragmentId}> is already locked by ${currentLock.user.name}. Cannot acquire lock.`);
      }

      return null;
    },
    [fetchLatest, sendThreadMessage, prepareLockMessage, lockingUser.id, refetchThread]
  );

  const releasePageFragment = useCallback(
    async (fragmentId: string, forcedBySystem: boolean = false) => {
      // Fetch the latest state for the thread
      const [latest = null] = await fetchLatest();
      const updatedFragmentLockState = latest ? (latest.payload as FragmentsLockState) : {};
      const currentLock = updatedFragmentLockState[fragmentId];

      if (currentLock?.user?.id !== lockingUser.id) {
        console.warn(
          `Fragment <${fragmentId}> is locked by ${currentLock?.user?.name ?? 'another user'}. Cannot release lock.`
        );

        return null;
      }

      // Only send a release message if the fragment is currently locked by the user

      const preparedLockMsg = prepareLockMessage(fragmentId, forcedBySystem);
      preparedLockMsg[fragmentId].user = null; // Set user to null to indicate release

      if (forcedBySystem) {
        preparedLockMsg[fragmentId].actor = 'curation';
      }

      const result = await sendThreadMessage({
        ...updatedFragmentLockState,
        ...preparedLockMsg
      });

      await refetchThread();

      return result;
    },
    [fetchLatest, lockingUser.id, prepareLockMessage, sendThreadMessage, refetchThread]
  );

  useEffect(() => {
    const [latest, prev] = threadMessages;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    setLockState(({ ...latest?.payload } as FragmentsLockState) ?? {});
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    setPrevLockState(({ ...prev?.payload } as FragmentsLockState) ?? {});
  }, [threadMessages]);

  const fragmentStates = useMemo(() => {
    const result: Record<string, FragmentStateData> = {};

    fragments.forEach((fragmentId) => {
      result[fragmentId] = fragmentState(fragmentId);
    });

    return result;
  }, [fragmentState, fragments]);

  const value: PageLockState = useMemo(
    () => ({
      state: fragmentStates,
      lockPageFragment,
      releasePageFragment,
      loading,
      refetcher
    }),
    [fragmentStates, lockPageFragment, releasePageFragment, loading, refetcher]
  );

  return <PageLockContext.Provider value={value}>{children}</PageLockContext.Provider>;
};
